import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const CandidateSummaryCard = ({ candidate }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'interview-ready':
        return 'status-excellent';
      case 'under-review':
        return 'status-good';
      case 'pending':
        return 'status-needs-review';
      case 'rejected':
        return 'status-rejected';
      default:
        return 'status-needs-review';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 85) return 'text-success';
    if (score >= 70) return 'text-accent';
    if (score >= 50) return 'text-warning';
    return 'text-destructive';
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 card-elevation-base">
      {/* Profile Header */}
      <div className="flex items-start space-x-4 mb-6">
        <div className="relative">
          <Image
            src={candidate?.avatar}
            alt={candidate?.name}
            className="w-16 h-16 rounded-full object-cover"
          />
          <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-success rounded-full border-2 border-card flex items-center justify-center">
            <Icon name="Check" size={12} color="white" />
          </div>
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-semibold text-card-foreground mb-1">{candidate?.name}</h2>
          <p className="text-muted-foreground mb-2">{candidate?.position}</p>
          <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(candidate?.status)}`}>
            <Icon name="Circle" size={8} className="mr-2" />
            {candidate?.status?.replace('-', ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
          </div>
        </div>
      </div>
      {/* AI Eligibility Score */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium text-card-foreground">AI Eligibility Score</h3>
          <span className={`text-2xl font-bold ${getScoreColor(candidate?.aiScore)}`}>
            {candidate?.aiScore}%
          </span>
        </div>
        <div className="w-full bg-muted rounded-full h-2 mb-3">
          <div 
            className={`h-2 rounded-full transition-all duration-500 ${
              candidate?.aiScore >= 85 ? 'bg-success' :
              candidate?.aiScore >= 70 ? 'bg-accent' :
              candidate?.aiScore >= 50 ? 'bg-warning' : 'bg-destructive'
            }`}
            style={{ width: `${candidate?.aiScore}%` }}
          />
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Skills Match:</span>
            <span className="font-medium">{candidate?.skillsMatch}%</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Experience:</span>
            <span className="font-medium">{candidate?.experienceMatch}%</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Education:</span>
            <span className="font-medium">{candidate?.educationMatch}%</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Cultural Fit:</span>
            <span className="font-medium">{candidate?.culturalFit}%</span>
          </div>
        </div>
      </div>
      {/* Contact Information */}
      <div className="space-y-3">
        <h3 className="font-medium text-card-foreground mb-3">Contact Information</h3>
        <div className="flex items-center space-x-3 text-sm">
          <Icon name="Mail" size={16} className="text-muted-foreground" />
          <span className="text-card-foreground">{candidate?.email}</span>
        </div>
        <div className="flex items-center space-x-3 text-sm">
          <Icon name="Phone" size={16} className="text-muted-foreground" />
          <span className="text-card-foreground">{candidate?.phone}</span>
        </div>
        <div className="flex items-center space-x-3 text-sm">
          <Icon name="MapPin" size={16} className="text-muted-foreground" />
          <span className="text-card-foreground">{candidate?.location}</span>
        </div>
        <div className="flex items-center space-x-3 text-sm">
          <Icon name="Linkedin" size={16} className="text-muted-foreground" />
          <a href={candidate?.linkedin} className="text-primary hover:underline">
            LinkedIn Profile
          </a>
        </div>
      </div>
      {/* Application Details */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground block">Applied Date</span>
            <span className="font-medium text-card-foreground">{candidate?.appliedDate}</span>
          </div>
          <div>
            <span className="text-muted-foreground block">Experience</span>
            <span className="font-medium text-card-foreground">{candidate?.experience}</span>
          </div>
          <div>
            <span className="text-muted-foreground block">Salary Expected</span>
            <span className="font-medium text-card-foreground">{candidate?.salaryExpected}</span>
          </div>
          <div>
            <span className="text-muted-foreground block">Notice Period</span>
            <span className="font-medium text-card-foreground">{candidate?.noticePeriod}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateSummaryCard;